export const AdminQueryKeys = {
  system: {
    health: ["admin", "system", "health"] as const,
  },
  songs: {
    search: (q: string, limit?: number, offset?: number, status?: string) =>
      ["admin", "songs", "search", q, limit, offset, status] as const,
    detail: (id: string) => ["admin", "songs", id] as const,
    events: (id: string) => ["admin", "songs", id, "events"] as const,
    all: ["admin", "songs"] as const,
  },
  users: {
    search: (q: string, limit?: number, offset?: number) =>
      ["admin", "users", "search", q, limit, offset] as const,
    detail: (id: string) => ["admin", "users", id] as const,
    songs: (id: string) => ["admin", "users", id, "songs"] as const,
    all: ["admin", "users"] as const,
  },
  diagnostics: {
    summary: ["admin", "diagnostics", "summary"] as const,
    category: (cat: string) => ["admin", "diagnostics", cat] as const,
    problems: (limit?: number, offset?: number) =>
      ["admin", "diagnostics", "problems", limit, offset] as const,
    diagnosis: (songId: string) =>
      ["admin", "diagnostics", "problems", songId, "diagnosis"] as const,
    uploads: (status?: string, limit?: number, offset?: number) =>
      ["admin", "diagnostics", "uploads", status, limit, offset] as const,
    uploadDetail: (id: string) => ["admin", "diagnostics", "uploads", id] as const,
    all: ["admin", "diagnostics"] as const,
  },
  jobs: {
    health: ["admin", "jobs", "health"] as const,
    failed: ["admin", "jobs", "failed"] as const,
    pending: ["admin", "jobs", "pending"] as const,
    all: ["admin", "jobs"] as const,
  },
  seed: {
    status: ["admin", "seed", "status"] as const,
    all: ["admin", "seed"] as const,
  },
  briefs: {
    search: (q: string, limit?: number, offset?: number, status?: string) =>
      ["admin", "briefs", "search", q, limit, offset, status] as const,
    detail: (id: string) => ["admin", "briefs", id] as const,
    submissions: (id: string) => ["admin", "briefs", id, "submissions"] as const,
    all: ["admin", "briefs"] as const,
  },
  // Suffix `"v2-promo"` on Soundscore + reports keys so clients flush stale
  // V1-shape cached responses on deploy of the Soundscore V2 promotion
  // (spec §2 R2 / §4a / §5). Any future wire-shape cut-over on these
  // endpoints should bump the suffix again (e.g. "v3").
  reports: {
    search: (q: string, limit?: number, offset?: number, status?: string) =>
      ["admin", "reports", "v2-promo", "search", q, limit, offset, status] as const,
    detail: (id: string) => ["admin", "reports", "v2-promo", id] as const,
    all: ["admin", "reports", "v2-promo"] as const,
  },
  soundscore: {
    overview: ["admin", "soundscore", "v2-promo", "overview"] as const,
    users: {
      search: (q: string, limit?: number, offset?: number) =>
        ["admin", "soundscore", "v2-promo", "users", "search", q, limit, offset] as const,
      detail: (id: string) => ["admin", "soundscore", "v2-promo", "users", id] as const,
    },
    leaderboard: {
      stats: ["admin", "soundscore", "v2-promo", "leaderboard", "stats"] as const,
      entries: (limit?: number, offset?: number) =>
        ["admin", "soundscore", "v2-promo", "leaderboard", "entries", limit, offset] as const,
    },
    activity: (category?: string, limit?: number, offset?: number) =>
      ["admin", "soundscore", "v2-promo", "activity", category, limit, offset] as const,
    scaling: {
      overview: ["admin", "soundscore", "scaling", "overview"] as const,
      analysis: ["admin", "soundscore", "scaling", "analysis"] as const,
    },
    all: ["admin", "soundscore", "v2-promo"] as const,
  },
  feedback: {
    search: (q: string, limit?: number, offset?: number, category?: string, status?: string) =>
      ["admin", "feedback", "search", q, limit, offset, category, status] as const,
    detail: (id: string) => ["admin", "feedback", id] as const,
    all: ["admin", "feedback"] as const,
  },
  featureFlags: {
    all: ["admin", "feature-flags"] as const,
  },
  catalog: {
    stats: ["admin", "catalog", "stats"] as const,
    sweepStatus: ["admin", "catalog", "sweep", "status"] as const,
    search: (
      q: string,
      type?: string,
      processingState?: string,
      analysisState?: string,
      healthStatus?: string,
      limit?: number,
      offset?: number,
    ) =>
      [
        "admin",
        "catalog",
        "search",
        q,
        type,
        processingState,
        analysisState,
        healthStatus,
        limit,
        offset,
      ] as const,
    stale: () => ["admin", "catalog", "streaming", "stale"] as const,
    sonoMissing: () => ["admin", "catalog", "sono", "missing"] as const,
    sonoStale: () => ["admin", "catalog", "sono", "stale"] as const,
    qualityMissing: () => ["admin", "catalog", "quality-validation", "missing"] as const,
    detail: (hash: string) => ["admin", "catalog", hash] as const,
    store: (hash: string) => ["admin", "catalog", hash, "store"] as const,
    health: (hash: string) => ["admin", "catalog", hash, "health"] as const,
    all: ["admin", "catalog"] as const,
  },
  soundscoreReports: {
    search: (
      q: string,
      version?: string,
      status?: string,
      staleOnly?: boolean,
      limit?: number,
      offset?: number,
    ) =>
      [
        "admin",
        "soundscore-reports",
        "search",
        q,
        version,
        status,
        staleOnly,
        limit,
        offset,
      ] as const,
    staleCount: ["admin", "soundscore-reports", "stale-count"] as const,
    metricsAggregate: ["admin", "soundscore-reports", "metrics", "aggregate"] as const,
    metrics: (id: string) => ["admin", "soundscore-reports", id, "metrics"] as const,
    projection: (id: string) => ["admin", "soundscore-reports", id, "projection"] as const,
    all: ["admin", "soundscore-reports"] as const,
  },
  soundscoreArtwork: {
    metricsAggregate: ["admin", "soundscore-artwork", "metrics", "aggregate"] as const,
    all: ["admin", "soundscore-artwork"] as const,
  },
  critiques: {
    list: (params?: object) => ["admin", "soundscore", "critiques", "list", params] as const,
    detail: (id: string) => ["admin", "soundscore", "critiques", id] as const,
    stats: ["admin", "soundscore", "critiques", "stats"] as const,
    all: ["admin", "soundscore", "critiques"] as const,
  },
  errors: {
    dashboard: (since?: string) => ["admin", "errors", "dashboard", since] as const,
    domain: (domain: string, since?: string) =>
      ["admin", "errors", "domain", domain, since] as const,
    events: (params?: object) => ["admin", "errors", "events", params] as const,
    grouped: (params?: object) => ["admin", "errors", "grouped", params] as const,
    detail: (id: string) => ["admin", "errors", id] as const,
    related: (correlationId?: string | null) =>
      ["admin", "errors", "related", correlationId] as const,
    config: ["admin", "errors", "config"] as const,
    all: ["admin", "errors"] as const,
  },
  billing: {
    overview: ["admin", "billing", "overview"] as const,
    tokenConsumption: (days: number) =>
      ["admin", "billing", "analytics", "token-consumption", days] as const,
    revenue: (days: number) => ["admin", "billing", "analytics", "revenue", days] as const,
    subscriptions: ["admin", "billing", "analytics", "subscriptions"] as const,
    transactions: (days: number) =>
      ["admin", "billing", "analytics", "transactions", days] as const,
    accounts: {
      search: (q: string, limit?: number, offset?: number) =>
        ["admin", "billing", "accounts", "search", q, limit, offset] as const,
      detail: (userId: string) => ["admin", "billing", "accounts", userId] as const,
      transactions: (userId: string, first?: number, after?: string, category?: string) =>
        ["admin", "billing", "accounts", userId, "transactions", first, after, category] as const,
    },
    products: {
      tokenPacks: ["admin", "billing", "products", "token-packs"] as const,
      subscriptionPlans: ["admin", "billing", "products", "subscription-plans"] as const,
    },
    pricing: {
      prices: ["admin", "billing", "pricing", "prices"] as const,
      rules: ["admin", "billing", "pricing", "rules"] as const,
    },
    campaigns: {
      list: ["admin", "billing", "campaigns"] as const,
      impact: (id: string) => ["admin", "billing", "campaigns", id, "impact"] as const,
    },
    all: ["admin", "billing"] as const,
  },
  avaConversations: {
    metricsAggregate: ["admin", "ava-conversations", "metrics", "aggregate"] as const,
    search: (
      q: string,
      agentType?: string,
      userId?: string,
      status?: string,
      limit?: number,
      offset?: number,
    ) =>
      [
        "admin",
        "ava-conversations",
        "search",
        q,
        agentType,
        userId,
        status,
        limit,
        offset,
      ] as const,
    detail: (id: string) => ["admin", "ava-conversations", id, "metrics"] as const,
    userMetrics: (userId: string) =>
      ["admin", "ava-conversations", "users", userId, "metrics"] as const,
    all: ["admin", "ava-conversations"] as const,
  },
  systemAssets: {
    registry: ["admin", "system-assets", "registry"] as const,
    all: ["admin", "system-assets"] as const,
  },
} as const;
